/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-console */
/* Define the cart component */
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Divider from "@mui/material/Divider";
import { deleteCart } from "./deleteCartSlice";
import { checkoutCart } from "./CartSlice";
import Toppings from "../topping-customization/Toppings";
import AddRemoveDuplicate from "./AddRemoveDuplicate";
import { putItemList } from "../item/item-list/putItemListSlice";
import { getContentHeight } from "../../utils/utils";

/* define custom styles */
const StyledBox = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  position: "absolute",
  background: "#212121",
}));

function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuHeight, setMenuHeight] = useState();
  const itemsInCart = useSelector((state) => state.cart.data);
  const totalOrderCost = useSelector((state) => state.cart.totalBill);
  const sessionId = useSelector((state) => state.uuid.sessionId);

  const handleWindowSizeChange = () => {
    setMenuHeight(getContentHeight());
  };

  const cartStyle = {
    overflow: "auto",
    width: "100vw",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, max-content))",
    display: "flex",
    flexGrow: 1,
    height: menuHeight,
    background: "#212121",
    boxShadow: "3px 17px 23px rgba(0, 0, 0, 0.7)",
    borderRadius: "3px",
  };

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    if(sessionId) {
      const putPayload = {
        items: itemsInCart,
        view: "CART_VIEW",
      };
      dispatch(putItemList(putPayload));
    }
  }, [itemsInCart, sessionId]);

  /* execute to clear or delete the current cart */
  const handleClearOrder = () => {
    dispatch(deleteCart());
    navigate(-1);
  };
  /* execute to checkout with current cart */
  const handlePayNow = () => {
    window.cart = itemsInCart;
    window.totalOrderCost = totalOrderCost;
    dispatch(checkoutCart());
    navigate("/WELCOME_VIEW", { state: { showFeedback: true } });
  };
  return (
    <Grid
      container
      justifyContent='flex-start'
      alignItems='flex-start'
      sx={cartStyle}
    >
      <Grid item md={9}>
        <AppBar position='sticky' sx={{ background: "#212121" }}>
          <Toolbar>
            <Typography variant='h5' sx={{ flexGrow: 1 }}>
              My Order ({itemsInCart.length})
            </Typography>
            <IconButton
              edge='start'
              color='inherit'
              onClick={() => {
                navigate(-1);
              }}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box>
          <List sx={{ marginBottom: 15 }}>
            {itemsInCart.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${index}_cart`}>
                <ListItem
                  secondaryAction={
                    <Typography variant='h5' style={{ marginRight: 15 }}>
                      ${item.price.toFixed(2)}
                    </Typography>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      alt='Profile Picture'
                      src={item.image_loc}
                      sx={{
                        borderRadius: 0,
                        objectFit: "contain",
                        width: 110,
                        height: 110,
                        marginRight: 1,
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <>
                        <Typography variant='h5'>{`${item.name}, ${item.size}`}</Typography>
                        <Typography
                          variant='h5'
                          color='text.secondary'
                          style={{
                            fontSize: "0.9rem",
                            marginRight: 2,
                            maxWidth: "90%",
                          }}
                        >
                          {`Estimated ${item.calories.toFixed(0)} Calories`}
                          <br />
                          <Toppings toppings={item.toppings} />
                        </Typography>
                        <AddRemoveDuplicate
                          itemQuantity={item.quantity}
                          item={item}
                        />
                      </>
                    }
                  />
                </ListItem>
                {itemsInCart.length !== index + 1 && (
                  <Divider
                    variant='inset'
                    component='li'
                    // eslint-disable-next-line react/no-array-index-key
                    key={`divider_${index}`}
                    sx={{ width: "97%", border: "1px solid #616161", m: 2 }}
                  />
                )}
              </div>
            ))}
          </List>
        </Box>
      </Grid>
      <Grid item md={3}>
        <StyledBox>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              background:
                "linear-gradient(270deg, #424242 0%, rgba(66, 66, 66, 0) 82.63%)",
            }}
          >
            <Typography
              variant='h6'
              paddingX={2}
              paddingY={1}
              sx={{
                marginRight: 2,
              }}
            >
              Total
            </Typography>
            <Typography
              variant='h5'
              paddingX={2}
              paddingY={1}
              sx={{
                marginRight: 2,
              }}
            >
              ${totalOrderCost}
            </Typography>
          </Box>
          <Box
            disableSpacing
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 2,
              marginRight: 3,
            }}
          >
            <Button
              variant='text'
              color='error'
              sx={{ m: 1 }}
              onClick={handleClearOrder}
            >
              Clear Order
            </Button>
            <Button
              variant='contained'
              sx={{ m: 1 }}
              onClick={handlePayNow}
              disabled={totalOrderCost === 0}
            >
              Pay Now
            </Button>
          </Box>
        </StyledBox>
      </Grid>
    </Grid>
  );
}

export default Cart;
